import StaffDirectory from "./Component/StaffDirectory";
import StudentProfile from "./Component/StudentProfile";
import Announcement from "./Component/Announcement";
import HallOfFrame from "./Component/HallOfFrame";
import CelebrationBanner from "./Component/CelebrationBanner";
import ManagementTalk from "./Component/ManagementTalk";
import OpinionPoll from "./Component/OpinionPoll";
import NewsBlogs from "./Component/NewsBlog";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import AxiosInstance, {
  TokenService,
} from "../../services/Api";
import { useSelector } from "react-redux";
import "./SchoolCockpitDashboard.css";
import "./SchoolCockpitDashboard.theme.css"; // visual theme only (no logic)

function SchoolCockpitDashboard() {
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || [];
  const school_id = clientData?.school_id || "";
  // const academic_year = clientData?.academic_year || "";
  const roleId = user?.role?.id || "";
  // const userId = user?.id || "";
  const master_id = clientData?.master_id || "";

  // ─── Role checks ───
  const isSchoolCockpit = roleId === 5;
  const isGroupCockpit = roleId === 2;
  const isStaff = roleId === 3;
  const isStudent = roleId === 4;
  const isParent = roleId === 6;
  const showStaffDirectory = isSchoolCockpit || isGroupCockpit || isStaff;

  // ─── State ───
  const [searchTerm, setSearchTerm] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [managementTalk, setManagementTalk] = useState([]);
  const [hallOfFame, setHallofFame] = useState([]);
  const [opinionPolls, setOpinionPolls] = useState([]);
  const [newsBlogs, setNewsBlogs] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [studentDetails, setStudentDetails] = useState([]);

  // ─── Staff Directory (API) ───
  const fetchStaffList = useCallback(async () => {
    if (!showStaffDirectory) return;
    setStaffLoading(true);
    const data = {
      id: school_id,
      page: 1,
      limit: 50,
      search: searchTerm,
    };
    try {
      const res = await AxiosInstance.post("school/staff/list", data);
      if (res.data?.status) {
        const mapped = res.data.data.map((item) => ({
          id: item.id,
          name: item.name,
          phone: item.mobile_no?.toString() || "",
          designation: item.designation || "",
          image: item.image || "",
        }));
        setStaffList(mapped);
      }
    } catch (error) {
      console.error("Failed to fetch staff list", error);
    } finally {
      setStaffLoading(false);
    }
  }, [school_id, searchTerm, showStaffDirectory]);

  // ─── Announcement ───
  const fetchAnnouncement = useCallback(async () => {
    const data = { school_id: school_id, id: "", search: "" };
    try {
      const res = await AxiosInstance.post("school/listAnnouncement", data);
      if (res.data?.status) {
        const mappedRows = res.data.data.map((item) => ({
          id: item.id,
          subject: item.subject,
          content: item.content,
          date: item.created_at,
        }));
        setRows(mappedRows);
      }
    } catch (error) {
      console.error("Failed to fetch announcement list", error);
    }
  }, [school_id]);

  // ─── Hall of Fame ───
  const fetchHallOfFame = useCallback(async () => {
    const data = { school_id: school_id, id: "", search: "" };
    try {
      const res = await AxiosInstance.post("/school/listFame", data);
      if (res.data?.status) {
        const mappedRows = res.data.data.map((item) => ({
          id: item.id,
          image: item.image || "",
          name: item.name,
          subject: item.subject,
          content: item.content,
        }));
        setHallofFame(mappedRows);
      }
    } catch (error) {
      console.error("Failed to fetch hall of fame list", error);
    }
  }, [school_id]);

  // ─── Management Talk ───
  const fetchManagementTalk = useCallback(async () => {
    const data = { school_id: school_id, id: "", search: "" };
    try {
      const res = await AxiosInstance.post("school/listManagementTalk", data);
      if (res.data?.status) {
        const mappedRows = res.data.data.map((item) => ({
          id: item.id,
          image: item.image || "",
          name: item.name,
          subject: item.subject,
          designation: item.designation,
          content: item.content,
          date: item.cdate || item.updated_at,
        }));
        setManagementTalk(mappedRows);
      }
    } catch (error) {
      console.error("Failed to fetch management talk list", error);
    }
  }, [school_id]);

  // ─── Opinion Poll ───
  const fetchOpinionPolls = useCallback(async () => {
    const data = { school_id: school_id, id: "", search: "" };
    try {
      const res = await AxiosInstance.post("school/listOpinionPoll", data);
      if (res.data?.status) {
        setOpinionPolls(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch opinion polls", error);
    }
  }, [school_id]);

  // ─── News & Blogs ───
  const fetchNewsBlogs = useCallback(async () => {
    const data = { school_id: school_id, id: "", search: "" };
    try {
      const res = await AxiosInstance.post("school/listNews", data);
      if (res.data?.status) {
        const mappedRows = res.data.data.map((item) => ({
          id: item.id,
          subject: item.subject,
          content: item.content,
          link: item.link || "",
          image: item.image || "",
          date: item.created_at,
        }));
        setNewsBlogs(mappedRows);
      }
    } catch (error) {
      console.error("Failed to fetch news & blogs", error);
    }
  }, [school_id]);

  // ─── Birthdays ───
  const fetchBirthdays = useCallback(async () => {
    if (!school_id) return;

    const data = {
      school_id: parseInt(school_id),
      date: moment().format("YYYY-MM-DD"),
    };

    try {
      const res = await AxiosInstance.post("school/birthdays", data);
      if (res.data?.status) {
        setBirthdays(res.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch birthdays", error);
    }
  }, [school_id]);

  // ─── Parent: Get student details ───
  const fetchParentStudents = useCallback(async () => {
    if (!isParent) return;
    const data = { parent_id: master_id };
    try {
      const res = await AxiosInstance.post("attendance/parent/students", data);
      if (res.data?.status) {
        setStudentDetails(res.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch parent students", error);
    }
  }, [isParent, master_id]);

  // ─── Effects ───
  useEffect(() => {
    fetchAnnouncement();
    fetchHallOfFame();
    fetchManagementTalk();
    fetchOpinionPolls();
    fetchNewsBlogs();
    fetchBirthdays();
  }, [
    fetchAnnouncement,
    fetchHallOfFame,
    fetchManagementTalk,
    fetchOpinionPolls,
    fetchNewsBlogs,
    fetchBirthdays,
  ]);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  useEffect(() => {
    fetchParentStudents();
  }, [fetchParentStudents]);

  // ─── Handlers ───
  const handleRefresh = () => {
    fetchAnnouncement();
  };

  const allAnnouncement = () => {
    navigate("/user/ViewAllAnnouncement", {
      state: { defaultTab: "ALL" },
    });
  };

  const hanldeNavigate = (data) => {
    navigate("/user/ViewAllAnnouncement", {
      state: { defaultTab: "ANNOUNCEMENTS", ID: data.id },
    });
  };

  // ─── Render ───
  return (
    <div className="contentWrapper dashboardWrapper">
      {/* TOP ROW */}
      <div className="row mb-4 gx-3">
        {/* LEFT COLUMN */}
        <div className="col-12 col-md-4 mobileCard mb-3 mb-md-0 px-2">
          {showStaffDirectory && (
            <StaffDirectory
              filteredStaff={staffList}
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
              title={t.staffDirectory}
              loading={staffLoading}
            />
          )}
          {isStudent && <StudentProfile user={user} title={t.profileTitle} />}
          {isParent && (
            <StudentProfile
              user={user}
              title={t.studentDetails}
              isParent={true}
              studentDetails={studentDetails}
            />
          )}
        </div>

        {/* MIDDLE COLUMN */}
        <div className="col-12 col-md-4 mobileCard mb-3 mb-md-0 px-2">
          <Announcement
            handleRefresh={handleRefresh}
            announcements={rows}
            hanldeNavigate={hanldeNavigate}
            handleAllAnnouncement={allAnnouncement}
            title={t.announcements}
            allDetails={t.announcements}
          />
        </div>

        {/* RIGHT COLUMN */}
        <div className="col-12 col-md-4 px-2">
          <HallOfFrame hallOfFameData={hallOfFame} />
        </div>
      </div>

      {/* CELEBRATION BANNER ROW */}
      <div className="row ">
        <div className="col-lg-12 col-md-12">
          <CelebrationBanner birthdays={birthdays} />
        </div>
      </div>

      {/* MANAGEMENT TALK + OPINION POLL ROW */}
      <div className="row mb-4">
        <div className="col-md-8 col-12 pe-md-2 mb-4 mb-md-0">
          <ManagementTalk managementTalks={managementTalk} />
        </div>
        <div className="col-md-4 col-12 ps-md-2">
          <OpinionPoll opinionPolls={opinionPolls} />
        </div>
      </div>

      {/* NEWS & BLOGS ROW */}
      <div className="row ">
        <div className="col-lg-12 col-md-12 col-12  ">
          <NewsBlogs newsBlogs={newsBlogs} />
        </div>
      </div>
    </div>
  );
}

export default SchoolCockpitDashboard;
